import { useEffect, useState } from 'react';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';

interface Exchange {
  id: number;
  name: string;
  logo: string;
  rating: number;
  description: string;
  supportedCoins: string;
  spotFees: string;
  futuresFees: string;
  kycRequirements: string;
  bonus: string;
  minDeposit: string;
  tradingFees2: string;
  visitUrl: string;
}

export default function TopCryptoExchanges() {
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchExchanges() {
      try {
        const res = await fetch(
          'https://blog.thecryptotutor.com/wp-json/wp/v2/crypto-exchange?per_page=50&_fields=id,title,acf,yoast_head_json'
        );
        const data = await res.json();

        const mapped: Exchange[] = data.map((post: any) => {
          const depositAndFees =
            post.acf?.deposit_and_fees?.split('<br>') || ['No Minimum Deposit', 'No Trading Fees'];

          return {
            id: post.id,
            name: post.title.rendered,
            logo:
              post.acf?.logo ||
              post.yoast_head_json?.og_image?.[0]?.url ||
              'https://via.placeholder.com/60',
            rating: post.acf?.rating || 5.0,
            description: post.acf?.excerpt || 'Top Cryptocurrency Exchange',
            supportedCoins: post.acf?.tradable_coins || 'N/A',
            spotFees: post.acf?.trading_fees || 'N/A',
            futuresFees: post.acf?.futures_fees || 'N/A',
            kycRequirements: post.acf?.kyc_requirements || 'N/A',
            bonus: post.acf?.bonus || 'N/A',
            minDeposit: depositAndFees[0],
            tradingFees2: depositAndFees[1],
            visitUrl: post.acf?.visit_platform_url || '#',
          };
        });

        setExchanges(mapped);
        setLoading(false);
      } catch (e) {
        console.error('Error fetching crypto exchanges:', e);
        setLoading(false);
      }
    }

    fetchExchanges();
  }, []);

  if (loading) return <div className="text-center py-10">Loading exchanges...</div>;

  return (
    <section className="py-10 bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg p-6 sm:p-8 shadow-sm">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8">
            Top Crypto Exchanges
          </h2>

          {/* FIXED GRID FOR TABLETS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {exchanges.slice(0, 3).map((exchange) => (
              <Card
                key={exchange.id}
                className="p-5 sm:p-6 flex flex-col justify-between bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
              >
                {/* Top */}
                <div className="flex justify-between items-center mb-3">
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <img
                      src={exchange.logo}
                      alt={exchange.name}
                      className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover"
                    />
                    <span className="text-base sm:text-lg font-semibold text-gray-900">
                      {exchange.name}
                    </span>
                  </div>

                  <div className="inline-flex items-center px-2.5 py-1 rounded-lg bg-[#F5F7FA] border border-gray-300 text-xs sm:text-sm font-medium text-gray-900">
                    <span>{exchange.rating.toFixed(1)}</span>
                    <i className="ri-star-fill text-yellow-400 ml-1"></i>
                  </div>
                </div>

                {/* Description */}
                <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
                  {exchange.description}
                </p>

                {/* Vertical Table */}
                <div className="bg-[#F5F7FA] rounded-lg text-xs sm:text-sm overflow-hidden mb-4 border border-gray-200">
                  <div className="flex justify-between px-3 py-2 border-b border-gray-300">
                    <span className="text-gray-500">Supported Coins</span>
                    <span className="font-medium">{exchange.supportedCoins}</span>
                  </div>
                  <div className="flex justify-between px-3 py-2 border-b border-gray-300">
                    <span className="text-gray-500">Spot Fees</span>
                    <span className="font-medium">{exchange.spotFees}</span>
                  </div>
                  <div className="flex justify-between px-3 py-2 border-b border-gray-300">
                    <span className="text-gray-500">Futures Fees</span>
                    <span className="font-medium">{exchange.futuresFees}</span>
                  </div>
                  <div className="flex justify-between px-3 py-2 border-b border-gray-300">
                    <span className="text-gray-500">KYC Requirements</span>
                    <span className="font-medium">{exchange.kycRequirements}</span>
                  </div>
                  <div className="flex justify-between px-3 py-2">
                    <span className="text-gray-500">Bonus</span>
                    <span className="font-medium">{exchange.bonus}</span>
                  </div>
                </div>

                {/* Min Deposit & Fees */}
                <div className="text-center text-[11px] sm:text-xs text-gray-500 mb-4 space-y-1">
                  <div>{exchange.minDeposit}</div>
                  <div>{exchange.tradingFees2}</div>
                </div>

                {/* Visit Button */}
                <Button
                  className="w-full rounded-lg py-3 text-sm font-medium"
                  onClick={() => window.open(exchange.visitUrl, '_blank')}
                >
                  Visit Platform
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
